import { z } from "zod";

import { isSlotInPast } from "~/shared/utils/dates";

/**
 * Error codes the backend returns when the chosen slot is no longer bookable:
 * - `no_availability_found`: the slot disappeared before the create call
 *   (re-check in StoreAppointmentAction found no availability).
 * - `slot_unavailable`: Healthie refused the create because the slot was taken
 *   in the race between availability check and booking (double-booking).
 *
 * Both mean the same thing to the user: refresh availability and pick again.
 */
const STALE_SLOT_ERROR_CODES = ["no_availability_found", "slot_unavailable"];

const bookingErrorSchema = z.object({
  response: z.object({
    data: z.object({
      error: z.object({
        code: z.string(),
        message: z.string().optional(),
        fields: z.record(z.string(), z.array(z.string())).optional(),
      }),
    }),
  }),
});

const parseBookingError = (error: unknown) => {
  const parsed = bookingErrorSchema.safeParse(error);

  return parsed.success ? parsed.data.response.data.error : undefined;
};

export const isStaleSlotError = (error: unknown): boolean => {
  const code = parseBookingError(error)?.code;

  return code !== undefined && STALE_SLOT_ERROR_CODES.includes(code);
};

/**
 * True when the booking failed because the signed booking token was missing,
 * tampered with, or expired (24-hour TTL). The backend rejects it as a
 * `validation_failed` 422 with a `bookingToken` field error. This is
 * unrecoverable in place — the patient's sign-up session is stale — so the UI
 * must send them back to restart rather than retry the same booking.
 */
export const isExpiredTokenError = (error: unknown): boolean => {
  const parsed = parseBookingError(error);

  return (
    parsed?.code === "validation_failed" &&
    (parsed.fields?.bookingToken?.length ?? 0) > 0
  );
};

export const getBookingErrorMessage = (error: unknown): string =>
  parseBookingError(error)?.message || "Something went wrong";

/**
 * The patient-facing wording for a refused-slot booking.
 *
 * Booking refuses an already-started slot with the same `slot_unavailable` code a
 * genuine double-booking race uses, so the code alone cannot say which happened.
 * Telling someone their slot was taken when the truth is that they sat on the page
 * past its start time reads as bad luck instead of a stale page, so judge it by the
 * slot's own time. Both endings ask for another pick, since the recovery is the
 * same: availability has been refreshed underneath them.
 */
export const getStaleSlotMessage = (
  selectedTime: string | null,
  now: number = Date.now(),
): string =>
  selectedTime !== null && isSlotInPast(selectedTime, now)
    ? "That time has already passed. We've refreshed availability — please pick another slot."
    : "That time was just booked by someone else. We've refreshed availability — please pick another slot.";
